import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchInvestitionenApi,
  createInvestitionApi,
  updateInvestitionApi,
  deleteInvestitionApi,
  fetchDokumenteApi,
  downloadDokumentApi,
  createDokumentApi,
  deleteDokumentApi,
} from './investitionenApi';
import {
  verschluessleJson,
  entschluessleJson,
  verschluessleBytes,
  entschluessleBytes,
} from 'core/features/sicherheit/crypto';
import { holeMasterKey } from 'core/features/sicherheit/vaultHolder';
import { sperren } from 'core/features/sicherheit/sicherheitSlice';
import { logout } from 'core/features/auth/authSlice';
import { fetchDepotApi } from 'modules/aktien';

/**
 * Redux-Slice fuer das Investitionen-Modul. Haelt die im Browser
 * ENTSCHLUESSELTEN Bereiche, ihre Positionen und Dokument-Metadaten (nur im
 * Speicher). Der Master-Key liegt Portal-weit im core-Feature "sicherheit". Alle
 * Thunks ver-/entschluesseln an der Grenze zur API; beim Sperren/Abmelden werden
 * die Inhalte verworfen. Aktuelle Werte koennen optional live aus dem Aktien-Depot
 * gezogen werden (Verknuepfung je Position ueber ISIN).
 */

// ── Positionen ──────────────────────────────────────────

/** Normalisiert eine einzelne Position (fehlende Felder auffuellen, id sichern). */
const normalisierePosition = (p) => ({
  id: p.id || crypto.randomUUID(),
  bezeichnung: p.bezeichnung || '',
  investiert: p.investiert ?? null,
  aktuellerWert: p.aktuellerWert ?? null,
  isin: p.isin || null,
  datum: p.datum || null,
  notiz: p.notiz || '',
});

/** Liefert die Positionen eines Bereichs; migriert Alt-Datensaetze (nur betrag). */
const positionenVon = (inv) => {
  if (Array.isArray(inv.positionen)) return inv.positionen.map(normalisierePosition);
  if (inv.betrag != null) {
    return [normalisierePosition({ bezeichnung: inv.institut || 'Position', investiert: inv.betrag, aktuellerWert: inv.betrag })];
  }
  return [];
};

// Inhaltliche Felder eines Bereichs, die verschluesselt abgelegt werden.
const inhaltVon = (inv) => ({
  institut: inv.institut || '',
  kategorie: inv.kategorie || '',
  waehrung: inv.waehrung || 'EUR',
  link: inv.link || '',
  notiz: inv.notiz || '',
  // Startguthaben des Barvermoegens (z. B. aktueller Kontostand beim Anlegen).
  anfangsbestand: inv.anfangsbestand != null ? Number(inv.anfangsbestand) : 0,
  positionen: positionenVon(inv),
});

/** Entschluesselt ein Bereichs-DTO in ein Anzeige-Objekt. */
const entschluessleInvestition = async (mk, dto) => {
  const inhalt = await entschluessleJson(mk, dto.cipherData, dto.iv);
  return {
    uuid: dto.uuid,
    sortierung: dto.sortierung,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    ...inhaltVon(inhalt),
  };
};

/** Baut das verschluesselte Wire-DTO aus einem Anzeige-Objekt. */
const baueDto = async (mk, inv) => {
  const blob = await verschluessleJson(mk, inhaltVon(inv));
  return { cipherData: blob.cipherText, iv: blob.iv, sortierung: inv.sortierung || 0 };
};

// ── Dokumente ───────────────────────────────────────────

/** Entschluesselt die Metadaten eines Dokument-DTOs (Inhalt bleibt am Server). */
const entschluessleDokumentMeta = async (mk, dto) => {
  const meta = await entschluessleJson(mk, dto.cipherMeta, dto.metaIv);
  return {
    uuid: dto.uuid,
    investitionUuid: dto.investitionUuid,
    name: meta.name,
    typ: meta.typ,
    jahr: meta.jahr ?? null,
    groesseOriginal: meta.groesse,
    groesse: dto.groesse,
    createdAt: dto.createdAt,
  };
};

// ── Thunks: Bereiche ────────────────────────────────────
export const ladeInvestitionen = createAsyncThunk('investitionen/laden', async () => {
  const dtos = await fetchInvestitionenApi();
  const mk = holeMasterKey();
  return Promise.all((dtos ?? []).map((d) => entschluessleInvestition(mk, d)));
});

export const speichereInvestition = createAsyncThunk('investitionen/speichern', async (inv) => {
  const mk = holeMasterKey();
  const dto = await baueDto(mk, inv);
  const gespeichert = inv.uuid ? await updateInvestitionApi(inv.uuid, dto) : await createInvestitionApi(dto);
  return {
    uuid: gespeichert.uuid,
    sortierung: gespeichert.sortierung,
    createdAt: gespeichert.createdAt,
    updatedAt: gespeichert.updatedAt,
    ...inhaltVon(inv),
  };
});

export const loescheInvestition = createAsyncThunk('investitionen/loeschen', async (uuid) =>
  deleteInvestitionApi(uuid)
);

// ── Thunks: Aktien-Depot-Verknuepfung ───────────────────

/** Laedt die Depot-Werte und indiziert sie nach ISIN (fuer verknuepfte Positionen). */
export const ladeDepotWerte = createAsyncThunk('investitionen/ladeDepotWerte', async () => {
  const depot = (await fetchDepotApi()) ?? { positionen: [] };
  const map = {};
  (depot.positionen ?? []).forEach((p) => {
    map[p.isin] = {
      aktuellerWert: p.aktuellerWert ?? null,
      gewinnAbsolut: p.gewinnAbsolut ?? null,
      gewinnProz: p.gewinnProz ?? null,
      aktienName: p.aktienName || p.isin,
      waehrung: p.anzeigeWaehrung || p.currency || 'EUR',
    };
  });
  return map;
});

// ── Thunks: Dokumente ───────────────────────────────────
export const ladeDokumente = createAsyncThunk('investitionen/ladeDokumente', async () => {
  const dtos = await fetchDokumenteApi();
  const mk = holeMasterKey();
  return Promise.all((dtos ?? []).map((d) => entschluessleDokumentMeta(mk, d)));
});

export const ladeDokumentHoch = createAsyncThunk(
  'investitionen/ladeDokumentHoch',
  async ({ file, investitionUuid, jahr }) => {
    const mk = holeMasterKey();
    const daten = await verschluessleBytes(mk, await file.arrayBuffer());
    const meta = await verschluessleJson(mk, {
      name: file.name,
      typ: file.type || 'application/octet-stream',
      groesse: file.size,
      jahr,
    });
    const dto = await createDokumentApi({
      investitionUuid,
      cipherMeta: meta.cipherText,
      metaIv: meta.iv,
      datenBase64: daten.datenBase64,
      datenIv: daten.datenIv,
    });
    return {
      uuid: dto.uuid,
      investitionUuid,
      name: file.name,
      typ: file.type || 'application/octet-stream',
      jahr,
      groesseOriginal: file.size,
      groesse: dto.groesse,
      createdAt: dto.createdAt,
    };
  }
);

export const ladeDokumentHerunter = createAsyncThunk('investitionen/ladeDokumentHerunter', async (dok) => {
  const voll = await downloadDokumentApi(dok.uuid);
  const buffer = await entschluessleBytes(holeMasterKey(), voll.datenBase64, voll.datenIv);
  const url = URL.createObjectURL(new Blob([buffer], { type: dok.typ || 'application/octet-stream' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = dok.name;
  link.click();
  URL.revokeObjectURL(url);
  return dok.uuid;
});

export const loescheDokument = createAsyncThunk('investitionen/loescheDokument', async (uuid) =>
  deleteDokumentApi(uuid)
);

const initialState = {
  investitionen: [],
  dokumente: [],
  depotWerte: {},
  status: 'idle',
  error: null,
};

/** Verwirft alle entschluesselten Inhalte (beim Sperren/Abmelden). */
const leereInhalte = (state) => {
  state.investitionen = [];
  state.dokumente = [];
  state.depotWerte = {};
  state.status = 'idle';
  state.error = null;
};

/** Ersetzt ein Element (per uuid) in der Liste oder haengt es an. */
const upsert = (liste, element) => {
  const idx = liste.findIndex((e) => e.uuid === element.uuid);
  if (idx >= 0) liste[idx] = element;
  else liste.push(element);
};

const investitionenSlice = createSlice({
  name: 'investitionen',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(sperren, leereInhalte)
      .addCase(logout, leereInhalte)
      .addCase(ladeInvestitionen.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(ladeInvestitionen.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.investitionen = action.payload;
      })
      .addCase(ladeInvestitionen.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(speichereInvestition.fulfilled, (state, action) => {
        upsert(state.investitionen, action.payload);
      })
      .addCase(loescheInvestition.fulfilled, (state, action) => {
        state.investitionen = state.investitionen.filter((i) => i.uuid !== action.payload);
        state.dokumente = state.dokumente.filter((d) => d.investitionUuid !== action.payload);
      })
      .addCase(ladeDepotWerte.fulfilled, (state, action) => {
        state.depotWerte = action.payload;
      })
      .addCase(ladeDokumente.fulfilled, (state, action) => {
        state.dokumente = action.payload;
      })
      .addCase(ladeDokumentHoch.fulfilled, (state, action) => {
        upsert(state.dokumente, action.payload);
      })
      .addCase(loescheDokument.fulfilled, (state, action) => {
        state.dokumente = state.dokumente.filter((d) => d.uuid !== action.payload);
      });
  },
});

// ── Selektoren ──────────────────────────────────────────
export const selectInvestitionen = (state) => state.investitionen.investitionen;
export const selectInvestitionenStatus = (state) => state.investitionen.status;
export const selectDokumente = (state) => state.investitionen.dokumente;
export const selectDepotWerte = (state) => state.investitionen.depotWerte;

export default investitionenSlice.reducer;
